import { h, JSX } from 'preact'
import { Message } from '../../../../lib/intl/Message'
import { FlertallInfo, Geografi, MandatInfo, PartiResultatMedMandat } from '../../../domain'
import { Graph } from './Graph/Graph'
import { Legend } from './Legend/Legend'
import { useWidgetUrls } from '../../../routes/widget'
import { Tooltip } from './Tooltip/Tooltip'
import { idFrom } from '../../../api'

type Props = {
  partier: PartiResultatMedMandat[]
  mandater?: MandatInfo
  flertall?: FlertallInfo
  geografi: Geografi
  widgetFooter?: JSX.Element
}

export function getFlertallAlone(
  partier: PartiResultatMedMandat[],
  mandat: MandatInfo
): FlertallInfo | undefined {
  const ingenMandatendring = mandat && mandat.endring === 0
  const sorted = partier
    .map(parti => {
      const mandaterForrige = (parti.mandater.antall || 0) - (parti.mandater.endring || 0)
      return { parti, mandaterForrige }
    })
    .sort((a, b) => b.mandaterForrige - a.mandaterForrige)

  const largest = sorted[0]

  const antallMandaterForrige = mandat.antall - mandat.endring
  const grenseFlertall = Math.ceil(antallMandaterForrige / 2)
  const isFlertall = largest.mandaterForrige >= grenseFlertall

  if (isFlertall && ingenMandatendring) {
    const antallMandater = largest.parti.mandater.antall || 0
    return {
      partier: [largest.parti.parti.id],
      antallMandater,
      antallMandaterOvrige: mandat.antall - antallMandater
    }
  }
}

export function BeholderDeFlertallet(props: Props) {
  const { flertall, partier, geografi, mandater, widgetFooter } = props
  const valgtype = 'kommune'
  const { beholderDeFlertalletUrl } = useWidgetUrls()

  if (!mandater) {
    return null
  }

  const flertallInfo = flertall || getFlertallAlone(partier, mandater)
  if (flertallInfo) {
    const hasFlertall = flertallInfo.antallMandater > flertallInfo.antallMandaterOvrige

    const resultater = partier.filter(({ parti }) => flertallInfo.partier.includes(parti.id))
    const ettParti = flertallInfo.partier.length === 1

    return (
      <section
        data-widget-url={beholderDeFlertalletUrl(geografi, valgtype)}
        data-valgtype={valgtype}
        data-grunndata={idFrom(geografi)}
        data-modulnavn="beholder-de-flertallet"
      >
        <Message
          as="h3"
          id="beholder-de-flertallet-tittel"
          modifiers={{ 'ett-parti': ettParti }}
          values={{ partiene: resultater.map(resultat => resultat.parti.navn) }}
        />
        <Message
          as="p"
          id="beholder-de-flertallet-intro"
          modifiers={{ 'ett-parti': ettParti }}
          values={{ partiene: resultater.map(resultat => resultat.parti.navn) }}
        />
        <Tooltip
          messageId={
            hasFlertall
              ? 'beholder-de-flertallet-tooltip-beholder-flertall'
              : 'beholder-de-flertallet-tooltip-beholder-ikke-flertall'
          }
          modifiers={{ 'ett-parti': ettParti }}
        />
        <Graph
          resultater={resultater}
          totaltAntallMandater={mandater.antall}
          forrigeTotaltAntallMandater={mandater.antall - mandater.endring}
        />
        <Legend
          message={ettParti ? undefined : <Message id="beholder-de-flertallet-legend-samarbeid" />}
          resultater={resultater}
        />
        <Message
          as="p"
          id="beholder-de-flertallet-outro"
          modifiers={{ 'ett-parti': ettParti }}
          values={{
            partiene: resultater.map(resultat => resultat.parti.kortNavn)
          }}
        />
        {widgetFooter}
      </section>
    )
  }

  return null
}
